import { useQuery } from "@tanstack/react-query";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import type { PortalJourneyTaskFormRow } from "./types";

type LoadFormResponse = { form: PortalJourneyTaskFormRow };

// GET /api/portal/forms/by-id/:formId
//
// Replaces /app/portal/journeys/_form_loader.ts::loadJourneyTaskFormAction.
// Used by Phase 7's <JourneyTaskFormDrawer> to lazy-load the form definition
// when the drawer opens for a task whose action.kind === 'form_submission'.
//
// The URL moved from `/portal/forms/:formId` to `/portal/forms/by-id/:formId`
// in Phase 7 sub-bucket 2a to make room for the new `GET /portal/forms/:slug`
// partner-detail handler (the bare `/:formId` segment now resolves to a
// slug-keyed lookup). Worker behavior is identical.
//
// The route 404s on draft/archived forms — partners must never see them.
// Forms are immutable once published from the partner's POV, so the cache
// stays valid forever; no cross-key invalidation is needed.
//
// `enabled` lets the caller defer the query until the drawer actually opens
// with a non-null formId (the typical pattern: pass `formId ?? null` from
// the row + `!!formId` as the enabled guard).
export function useJourneyTaskForm(formId: string | null) {
  return useQuery<PortalJourneyTaskFormRow, ApiClientError>({
    queryKey: ["portal", "journeys", "form", formId],
    queryFn: async () => {
      const res = await apiFetch<LoadFormResponse>(
        `/portal/forms/by-id/${formId}`,
      );
      return res.form;
    },
    enabled: !!formId,
    staleTime: 5 * 60 * 1000,
  });
}
